/**
 * Menções dentro de texto simples — chat do projeto e descrição da atividade.
 *
 * O Caderno tem editor rico (Tiptap) e guarda menção como nó. Chat e
 * descrição são texto: a menção vira um marcador no próprio texto, legível e
 * estável, que a tela desenha como chip e o banco lê com uma expressão
 * regular (`supply.mention_token_ids`) para vincular e avisar:
 *
 *   @[Ana Souza](p:<uuid>)     pessoa
 *   #[SUP-12](a:<uuid>)        tarefa
 *   ![Ata de sexta](n:<uuid>)  nota do Caderno
 *
 * O rótulo é o que se lia no momento da menção; o id é o que vale. Texto
 * antigo sem marcador continua funcionando (chave `SUP-12` solta vira chip
 * pelo `extractActivityKeys`).
 */

export type MentionTokenKind = 'pessoa' | 'atividade' | 'nota'

export interface MentionRef {
  readonly kind: MentionTokenKind
  readonly id: string
  readonly label: string
}

export type MentionSegment =
  | { readonly type: 'text'; readonly text: string }
  | ({ readonly type: 'mention' } & MentionRef)

const SIGIL: Record<MentionTokenKind, string> = { pessoa: '@', atividade: '#', nota: '!' }
const CODE: Record<MentionTokenKind, string> = { pessoa: 'p', atividade: 'a', nota: 'n' }
const KIND_OF_CODE: Record<string, MentionTokenKind> = { p: 'pessoa', a: 'atividade', n: 'nota' }

const UUID = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}'
/** Mesma expressão do banco (migration de comentários do Supply). Rótulo sem `]` e até 120 caracteres. */
const TOKEN = new RegExp(`([@#!])\\[([^\\]\\n]{1,120})\\]\\(([pan]):(${UUID})\\)`, 'g')

/** `]`, quebra de linha e excesso quebrariam o marcador; o rótulo é só exibição. */
export function cleanMentionLabel(label: string): string {
  return label.replace(/[\]\n\r]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 120) || '?'
}

export function mentionToken(ref: MentionRef): string {
  return `${SIGIL[ref.kind]}[${cleanMentionLabel(ref.label)}](${CODE[ref.kind]}:${ref.id})`
}

/** Como a menção aparece para quem lê (e no campo, enquanto se escreve). */
export function mentionDisplay(ref: Pick<MentionRef, 'kind' | 'label'>): string {
  return `${SIGIL[ref.kind] === '!' ? '📝' : SIGIL[ref.kind]}${cleanMentionLabel(ref.label)}`
}

export function parseMentionTokens(text: string): MentionSegment[] {
  const segments: MentionSegment[] = []
  let last = 0
  for (const match of text.matchAll(TOKEN)) {
    const at = match.index ?? 0
    if (at > last) segments.push({ type: 'text', text: text.slice(last, at) })
    const kind = KIND_OF_CODE[match[3] ?? '']
    const sigilOk = kind && SIGIL[kind] === match[1]
    if (kind && sigilOk) {
      segments.push({ type: 'mention', kind, label: match[2] ?? '', id: (match[4] ?? '').toLowerCase() })
    } else {
      segments.push({ type: 'text', text: match[0] })
    }
    last = at + match[0].length
  }
  if (last < text.length) segments.push({ type: 'text', text: text.slice(last) })
  return segments
}

/** Ids citados de um tipo, sem repetição, na ordem do texto. */
export function mentionedIds(text: string, kind: MentionTokenKind): string[] {
  const ids: string[] = []
  for (const segment of parseMentionTokens(text)) {
    if (segment.type === 'mention' && segment.kind === kind && !ids.includes(segment.id)) ids.push(segment.id)
  }
  return ids
}

/** Texto para leitura sem chips (aviso, busca): marcador vira `@Nome`, `#SUP-12`, `📝Título`. */
export function mentionPlainText(text: string): string {
  return parseMentionTokens(text)
    .map((s) => (s.type === 'text' ? s.text : mentionDisplay(s)))
    .join('')
}

/**
 * Do texto guardado para o campo de edição: o campo mostra `@Ana Souza`, e as
 * referências ficam à parte para voltar a virar marcador ao salvar.
 */
export function decodeMentions(stored: string): { text: string; refs: MentionRef[] } {
  const refs: MentionRef[] = []
  const text = parseMentionTokens(stored)
    .map((s) => {
      if (s.type === 'text') return s.text
      const { type: _t, ...ref } = s
      if (!refs.some((r) => r.kind === ref.kind && r.id === ref.id)) refs.push(ref)
      return mentionDisplay(ref)
    })
    .join('')
  return { text, refs }
}

/**
 * Do campo para o texto guardado: cada `@Ana Souza` que ainda está escrito
 * volta a ser marcador. Menção apagada do texto simplesmente some — não há
 * referência órfã. Rótulos mais longos primeiro, para `@Ana Souza Lima` não
 * ser engolido por `@Ana Souza`.
 */
export function encodeMentions(text: string, refs: readonly MentionRef[]): string {
  const ordered = [...refs].sort((a, b) => mentionDisplay(b).length - mentionDisplay(a).length)
  let out = text
  const placeholders: string[] = []
  for (const ref of ordered) {
    const display = mentionDisplay(ref)
    if (!out.includes(display)) continue
    const slot = `\u0000${placeholders.length}\u0000`
    placeholders.push(mentionToken(ref))
    out = out.split(display).join(slot)
  }
  // NUL delimita o marcador de propósito: não aparece em texto digitado.
  // eslint-disable-next-line no-control-regex
  return out.replace(/\u0000(\d+)\u0000/g, (_, i: string) => placeholders[Number(i)] ?? '')
}
